import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { teamQueries, vendorQueries } from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import { T, sans, card } from './VendorDetails';
import { LinkedInLink } from '../../components/LinkedInField';

/* The tenant's own record of a vendor, edited one section at a time through PATCH /vendors/{id}/profile
   (only the fields a section sends are written; the change is audit-logged). Shown on the Profile tab. */

type Section = 'business' | 'risk' | 'contract' | 'ownership';
type Values = Record<string, any>;

const LIFECYCLE: [string, string][] = [['due_diligence', 'Due diligence'], ['active_monitoring', 'Active monitoring'], ['offboarding', 'Offboarding']];
const SENSITIVITY: [string, string][] = [['none', 'No sensitive data'], ['internal', 'Internal data'], ['pii', 'Personal data (PII)'], ['financial', 'Financial data'], ['regulated', 'Regulated data']];
const PII_VOLUME: [string, string][] = [['lt_1k', 'Under 1,000'], ['1k_10k', '1,000 – 10,000'], ['10k_100k', '10,000 – 100,000'], ['100k_1m', '100,000 – 1 million'], ['gt_1m', 'Over 1 million'], ['unknown', 'Not known']];
const CRITICALITY: [string, string][] = [['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['critical', 'Critical']];
const ACCESS: [string, string][] = [['none', 'No access'], ['limited', 'Limited access'], ['privileged', 'Privileged access']];

const FIELDS: Record<Section, string[]> = {
  business: ['category', 'business_units', 'location', 'parent_company', 'vendor_since', 'lifecycle', 'service_description', 'tags'],
  risk: ['data_sensitivity', 'pii_record_volume', 'business_criticality', 'system_access', 'next_review_date'],
  contract: ['contract_start_date', 'contract_end_date', 'contract_value', 'ytd_spend', 'renewal_notice_days', 'auto_renew'],
  ownership: ['owner_user_id', 'business_owner'],
};

const labelOf = (opts: [string, string][], v?: string | null) => (v ? opts.find(o => o[0] === v)?.[1] || v : null);
const day = (v?: string | null) => (v ? String(v).slice(0, 10) : '');
const fmtDay = (v?: string | null) => {
  if (!v) return null;
  const [y, m, d] = String(v).slice(0, 10).split('-').map(Number);
  return y ? new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
};
const money = (v?: number | null) => (v == null ? null : `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);
const list = (v: string) => v.split(',').map(s => s.trim()).filter(Boolean);
const apiError = (e: any) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg).filter(Boolean).join('; ') || 'Could not save.';
  return typeof d === 'string' ? d : 'Could not save.';
};

export default function VendorRecord({ vendorId, profile, isDark }: { vendorId: string; profile: Values; isDark: boolean }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = !!user && user.role !== 'viewer';
  const [editing, setEditing] = useState<Section | null>(null);
  const [draft, setDraft] = useState<Values>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<Section | null>(null);
  const team = useQuery({ ...teamQueries.members(), enabled: editing === 'ownership' });

  const c = {
    ink: isDark ? '#e2e8f0' : T.ink, muted: isDark ? '#94a3b8' : T.slate, faint: isDark ? '#64748b' : T.muted,
    line: isDark ? '#1f1f1f' : T.bdr, soft: isDark ? '#141414' : T.surface2, accent: isDark ? '#60A5FA' : T.blue,
    input: isDark ? '#0a0a0a' : '#fff',
  };

  const start = (s: Section) => {
    const v: Values = {};
    for (const f of FIELDS[s]) {
      const raw = f === 'owner_user_id' ? profile.ownerUserId : f === 'business_owner' ? profile.businessOwner : profile[f];
      if (f === 'business_units' || f === 'tags') v[f] = (raw || []).join(', ');
      else if (/date|since/.test(f)) v[f] = day(raw);
      else if (f === 'auto_renew') v[f] = !!raw;
      else v[f] = raw ?? '';
    }
    setDraft(v); setError(null); setSaved(null); setEditing(s);
  };

  const save = useMutation({
    mutationFn: async (s: Section) => {
      const body: Values = {};
      for (const f of FIELDS[s]) {
        const v = draft[f];
        if (f === 'business_units' || f === 'tags') body[f] = list(v || '');
        else if (f === 'auto_renew') body[f] = !!v;
        else if (['contract_value', 'ytd_spend', 'renewal_notice_days'].includes(f)) body[f] = v === '' || v == null ? null : Number(v);
        else if (f === 'lifecycle') { if (v) body[f] = v; }
        else body[f] = v === '' ? null : v;
      }
      if (body.data_sensitivity !== 'pii' && s === 'risk') body.pii_record_volume = null;
      return (await api.patch(`/v1/vendors/${vendorId}/profile`, body)).data;
    },
    meta: { invalidates: [['vendors']] },
    onSuccess: (data, s) => {
      queryClient.setQueryData(vendorQueries.profile(vendorId).queryKey, data);
      setEditing(null); setSaved(s);
    },
    onError: (e: any) => setError(apiError(e)),
  });

  /* ── pieces ── */
  const lbl: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, color: c.faint, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 };
  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 7, border: `1px solid ${c.line}`,
    background: c.input, color: c.ink, fontFamily: sans, fontSize: 13, outline: 'none',
  };
  const btn = (primary: boolean): React.CSSProperties => ({
    padding: '7px 14px', borderRadius: 7, fontFamily: sans, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${primary ? T.blue : c.line}`, background: primary ? T.blue : 'transparent', color: primary ? '#fff' : c.ink,
  });
  const kv = (k: string, v: React.ReactNode, wide = false) => (
    <div key={k} style={{ minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>
      <div style={lbl}>{k}</div>
      <div style={{ fontSize: 13, color: v ? c.ink : c.faint, fontWeight: v ? 500 : 400, overflowWrap: 'anywhere', lineHeight: 1.5 }}>{v || 'Not set'}</div>
    </div>
  );
  const set = (f: string, v: any) => setDraft(d => ({ ...d, [f]: v }));
  const text = (k: string, f: string, opts: { type?: string; placeholder?: string; wide?: boolean; area?: boolean } = {}) => (
    <label key={f} style={{ minWidth: 0, gridColumn: opts.wide ? '1 / -1' : undefined }}>
      <div style={lbl}>{k}</div>
      {opts.area
        ? <textarea rows={3} value={draft[f] ?? ''} placeholder={opts.placeholder} onChange={e => set(f, e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
        : <input type={opts.type || 'text'} value={draft[f] ?? ''} placeholder={opts.placeholder} onChange={e => set(f, e.target.value)} style={inputStyle} min={opts.type === 'number' ? 0 : undefined} />}
    </label>
  );
  const select = (k: string, f: string, options: [string, string][], allowEmpty = true) => (
    <label key={f} style={{ minWidth: 0 }}>
      <div style={lbl}>{k}</div>
      <select value={draft[f] ?? ''} onChange={e => set(f, e.target.value)} style={inputStyle}>
        {allowEmpty && <option value="">Not set</option>}
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
  const chips = (items: string[]) => items.length ? (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 5 }}>
      {items.map(t => <span key={t} style={{ padding: '2px 9px', borderRadius: 99, fontSize: 11.5, fontWeight: 600, color: c.accent, background: isDark ? 'rgba(59,130,246,.12)' : T.blueBg, border: `1px solid ${isDark ? '#1e3a8a' : T.blueBdr}` }}>{t}</span>)}
    </span>
  ) : null;

  const panel = (s: Section, title: string, view: React.ReactNode, form: React.ReactNode, extra?: React.ReactNode) => (
    <div style={{ border: `1px solid ${c.line}`, borderRadius: 10, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>{title}</span>
        {extra}
        {saved === s && editing !== s && <span style={{ fontSize: 11.5, color: isDark ? '#4ADE80' : T.grn, fontWeight: 600 }}>✓ Saved</span>}
        {canEdit && editing !== s && (
          <button onClick={() => start(s)} disabled={!!editing}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', padding: 0, color: editing ? c.faint : c.accent, fontFamily: sans, fontSize: 12.5, fontWeight: 600, cursor: editing ? 'default' : 'pointer' }}>
            ✎ Edit
          </button>
        )}
      </div>
      {editing === s ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>{form}</div>
          {error && <div style={{ marginTop: 12, fontSize: 12.5, color: isDark ? '#F87171' : T.red }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
            <button style={btn(false)} disabled={save.isPending} onClick={() => { setEditing(null); setError(null); }}>Cancel</button>
            <button style={btn(true)} disabled={save.isPending} onClick={() => save.mutate(s)}>{save.isPending ? 'Saving…' : 'Save'}</button>
          </div>
        </>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>{view}</div>
      )}
    </div>
  );

  const tier = profile.inherent_risk?.tier as string | null;
  const tierTone: Record<string, [string, string]> = {
    Low: [T.grn, T.grnBg], Medium: [T.amb, T.ambBg], High: ['#C2410C', '#FFF7ED'], Critical: [T.red, T.redBg],
  };

  return (
    <section id="vp-record" style={{ ...card(isDark), scrollMarginTop: 120, fontFamily: sans }}>
      <style>{`.vr-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; } @media (max-width: 1180px) { .vr-grid { grid-template-columns: minmax(0, 1fr); } }`}</style>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: c.ink }}>Vendor record</div>
        <div style={{ fontSize: 11.5, color: c.muted, marginTop: 1 }}>
          Your team's details for this vendor. Used for inherent risk, renewals, reports and financial exposure.
          {!canEdit && ' You have view-only access.'}
        </div>
      </div>
      <div className="vr-grid">
        {panel('business', 'Business & relationship', <>
          {kv('Category', profile.category)}
          {kv('Lifecycle stage', labelOf(LIFECYCLE, profile.lifecycle))}
          {kv('Business units', chips(profile.business_units || []))}
          {kv('Location', profile.location)}
          {kv('Parent company', profile.parent_company)}
          {kv('Vendor since', fmtDay(profile.vendor_since))}
          {kv('LinkedIn page', profile.linkedinUrl ? (
            <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <LinkedInLink url={profile.linkedinUrl} size={14} />
                <a href={profile.linkedinUrl} target="_blank" rel="noopener noreferrer" style={{ color: c.accent, textDecoration: 'none', overflowWrap: 'anywhere' }}>
                  {String(profile.linkedinUrl).replace(/^https:\/\/www\./, '')}
                </a>
              </span>
              <span style={{ fontSize: 11, color: c.faint, fontWeight: 400 }}>
                Found automatically on their website
              </span>
            </span>
          ) : 'Not linked from their website', true)}
          {kv('What they do for you', profile.service_description, true)}
          {kv('Tags', chips(profile.tags || []), true)}
        </>, <>
          {text('Category', 'category', { placeholder: 'e.g. Cloud hosting' })}
          {select('Lifecycle stage', 'lifecycle', LIFECYCLE, false)}
          {text('Business units', 'business_units', { placeholder: 'Comma separated, e.g. Finance, HR' })}
          {text('Location', 'location', { placeholder: 'e.g. Bengaluru, India' })}
          {text('Parent company', 'parent_company')}
          {text('Vendor since', 'vendor_since', { type: 'date' })}
          {text('What they do for you', 'service_description', { area: true, wide: true, placeholder: 'The service this vendor provides' })}
          {text('Tags', 'tags', { wide: true, placeholder: 'Comma separated' })}
        </>)}

        {panel('risk', 'Risk classification', <>
          {kv('Data they handle', labelOf(SENSITIVITY, profile.data_sensitivity))}
          {profile.data_sensitivity === 'pii' && kv('Personal records a year', labelOf(PII_VOLUME, profile.pii_record_volume))}
          {kv('Business criticality', labelOf(CRITICALITY, profile.business_criticality))}
          {kv('Access to your systems', labelOf(ACCESS, profile.system_access))}
          {kv('Next review', fmtDay(profile.next_review_date))}
        </>, <>
          {select('Data they handle', 'data_sensitivity', SENSITIVITY)}
          {draft.data_sensitivity === 'pii' && select('Personal records a year', 'pii_record_volume', PII_VOLUME)}
          {select('Business criticality', 'business_criticality', CRITICALITY)}
          {select('Access to your systems', 'system_access', ACCESS)}
          {text('Next review', 'next_review_date', { type: 'date' })}
        </>, tier ? (
          <span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, color: tierTone[tier]?.[0], background: isDark ? 'transparent' : tierTone[tier]?.[1], border: `1px solid ${tierTone[tier]?.[0]}40` }}>
            Inherent risk: {tier}
          </span>
        ) : null)}

        {panel('contract', 'Contract & spend', <>
          {kv('Contract start', fmtDay(profile.contract_start_date))}
          {kv('Contract end', fmtDay(profile.contract_end_date))}
          {kv('Contract value', money(profile.contract_value))}
          {kv('Spend this year', money(profile.ytd_spend))}
          {kv('Renewal notice', profile.renewal_notice_days != null ? `${profile.renewal_notice_days} days` : null)}
          {kv('Auto-renews', profile.auto_renew ? 'Yes' : 'No')}
        </>, <>
          {text('Contract start', 'contract_start_date', { type: 'date' })}
          {text('Contract end', 'contract_end_date', { type: 'date' })}
          {text('Contract value (USD)', 'contract_value', { type: 'number' })}
          {text('Spend this year (USD)', 'ytd_spend', { type: 'number' })}
          {text('Renewal notice (days)', 'renewal_notice_days', { type: 'number' })}
          <label key="auto_renew" style={{ display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'end', paddingBottom: 8, fontSize: 13, color: c.ink, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!draft.auto_renew} onChange={e => set('auto_renew', e.target.checked)} />Auto-renews
          </label>
        </>)}

        {panel('ownership', 'Ownership', <>
          {kv('Owner', profile.owner)}
          {kv('Business owner', profile.businessOwner)}
        </>, <>
          <label key="owner" style={{ minWidth: 0 }}>
            <div style={lbl}>Owner</div>
            <select value={draft.owner_user_id ?? ''} onChange={e => set('owner_user_id', e.target.value)} style={inputStyle}>
              <option value="">Unassigned</option>
              {draft.owner_user_id && !(team.data || []).some(m => m.id === draft.owner_user_id) && <option value={draft.owner_user_id}>{profile.owner || 'Current owner'}</option>}
              {(team.data || []).map(m => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
            </select>
          </label>
          {text('Business owner', 'business_owner', { placeholder: 'Name of the person who owns the relationship' })}
        </>)}
      </div>
    </section>
  );
}
